import { forestEditorial } from './forest-editorial-content';
import { forestInformationDetails } from './forest-information-details';
import { informationDetailKey, type ResearchInformationDetail } from './research-information';

/** Public reading layer only. Editorial keys never identify an approved definition. */
export const forestReferenceNature = 'PUBLIC_RESEARCH_REFERENCE_PENDING_SCENARIO_OWNER';
export interface ResearchCapability {
  readonly key: string;
  readonly name: string;
  readonly purpose: string;
  readonly output: string;
  readonly method: string;
  readonly limit: string;
  readonly media: string;
  readonly sourceIds: readonly string[];
  readonly formalCapabilityId: null;
}
export interface ResearchIssue {
  readonly key: string;
  readonly title: string;
  readonly summary: string;
  readonly question: string;
  readonly media: string;
  readonly information: readonly {
    readonly name: string;
    readonly purpose: string;
    readonly acquisition: string;
    readonly sourceIds: readonly string[];
    /** 逐项详解：为什么需要、需要拿到什么、缺失会怎样、能说明与不能说明什么。 */
    readonly detail?: ResearchInformationDetail | undefined;
  }[];
  readonly capabilities: readonly ResearchCapability[];
  readonly sourceIds: readonly string[];
  readonly caseIds: readonly string[];
}
export type ForestSourceGroup = '法规与标准' | '实践与事故' | '技术参考';
export interface ForestSource {
  readonly id: string;
  readonly title: string;
  readonly publisher: string;
  readonly kind: string;
  readonly group: ForestSourceGroup;
  readonly url: string;
  readonly summary: string;
  readonly documentUrl?: string;
  readonly documentLabel?: string;
}
const summaries: Readonly<Record<string, string>> = {
  'CN-REG-822':
    '国务院令第822号，自2026-01-01施行。将预防、报告、巡护和人员保障的职责放在同一制度框架中，可用于理解各问题为何需要核实信息；不替代地方预案和具体授权。',
  'CN-MON-2024':
    '国森防发〔2024〕2号组织卫星、航空、视频、瞭望、巡查、舆情六位一体监测，并强调核查反馈和信息共享。用于理解多源线索、环境信息和通信协作；不代表本平台已接入这些数据。',
  'CN-SPEC-3437':
    'LY/T 3437—2025于2025-11-01实施。本页只引用目录状态，将火灾、热点、人员和通信等资料作为信息组织参考，不声称逐条核验标准正文或设备符合性。',
  'CN-UAV-47054':
    'GB/T 47054—2026于2026-05-01实施，官方目录标示为现行推荐国家标准。用于了解无人机巡查的人员、装备、安全与资料组织边界；不是具体机型的性能认证或飞行授权。',
  'CN-VIDEO-47058':
    'GB/T 47058—2026于2026-05-01实施，官方目录标示为现行推荐国家标准。用于理解视频监测的信息获取和系统组织，不能以目录状态证明监控覆盖、告警准确率或设备认证。',
  'CN-SAFETY-35':
    'YJ/T 35—2025提供地方森林草原消防队伍扑救安全参考。用于理解人员联络、信息核实与专业安全边界，不将研究卡片转为作战或疏散指令。',
  'CN-FIRE-PLAN-2020':
    '国办函〔2020〕99号用于查阅国家预案的历史组织结构。引用时需结合2026年现行条例复核，不能代替当地现行预案、人员职责或操作授权。',
  'CN-ANHUI-5':
    '官方汇编记载2025年3月安徽五起森林火灾（火情）。用于理解祭祀用火、施工吸烟等火源线索与核查的关系；具体事实按下方逐案摘要和原文查阅，不外推到当前现场。',
  'NASA-FIRMS':
    'NASA FIRMS资料说明卫星热点的观测位置、时间及查询字段。热点不等于已确认火灾或实时火场边界；本页仅提供技术文档入口，不调用监测接口。',
  'CN-FORESTRY-RELAY':
    '十八站林业局报道记录无人机接入测试及卫星便携站配合图像回传。用于理解偏远林区链路、电源和测通条件，是业务实践，不是火灾事故调查或本平台部署证明。',
  'CN-FORESTRY-DRONE':
    '报道中的宁波线上拉练明确模拟村庄火情，并介绍视频监测和无人机巡查。可用于理解线索发现与核实协作；模拟演练不构成实际火灾事实或设备认证。',
  'CN-FORESTRY-PATROL':
    '莆田报道介绍村居巡护、管护用房、视频和无人机巢等防火实践。用于理解巡护记录与火源管理，是业务实践，不是已确认火灾事故。',
};
export const forestSources: readonly ForestSource[] = forestEditorial.sources.map((source) => {
  const pdf = forestEditorial.pdfLinks.find(
    (row) => row.id.replace(/-(PDF|ONLINE)$/, '') === source.id,
  );
  const practice = source.kind === 'official_case' || source.id === 'CN-ANHUI-5';
  return {
    id: source.id,
    title: source.title,
    publisher: source.issuer,
    kind:
      source.id === 'CN-FORESTRY-DRONE'
        ? '模拟演练 · 官方报道'
        : source.kind === 'official_case'
          ? '业务实践 · 非火灾事故'
          : source.sourceType,
    group: practice
      ? '实践与事故'
      : source.kind === 'external_technical' || source.kind === 'official_media_page'
        ? '技术参考'
        : '法规与标准',
    url: source.officialUrl,
    summary:
      summaries[source.id] ?? `${source.usableFor}。仅作研究参考，不证明本地资源状态或授权。`,
    ...(pdf
      ? {
          documentUrl: pdf.directPdfUrl || pdf.sourcePage,
          documentLabel: pdf.directPdfUrl ? '查看官方 PDF 原文' : '进入官方标准预览',
        }
      : {}),
  };
});
export const forestCases = forestEditorial.cases.map((row) => ({
  ...row,
  kindLabel:
    row.kind === 'official_case'
      ? '实际森林火灾（火情）'
      : row.id === 'PRACTICE-DRONE-2026'
        ? '模拟演练 · 非实际火灾'
        : '业务实践 · 非火灾事故',
}));
const capabilitySources: Readonly<Record<string, readonly string[]>> = {
  'CAP-SIGNAL': ['CN-MON-2024', 'NASA-FIRMS'],
  'CAP-AIR': ['CN-UAV-47054', 'CN-UAV-EXPLAIN', 'CN-FORESTRY-DRONE'],
  'CAP-VIDEO': ['CN-VIDEO-47058', 'CN-MON-2024', 'CN-FORESTRY-WATCH'],
  'CAP-COMMS': ['CN-FORESTRY-RELAY', 'CN-MON-2024'],
  'CAP-METEO': ['CN-MON-2024', 'CN-REG-822'],
  'CAP-MULTISOURCE': ['CN-MON-2024', 'CN-SPEC-3437'],
  'CAP-GEO': ['CN-SPEC-3437', 'CN-REG-822'],
  'CAP-PERSON': ['CN-SAFETY-35', 'CN-REG-822'],
  'CAP-RELAY': ['CN-FORESTRY-RELAY', 'CN-MON-2024'],
  'CAP-PATROL': ['CN-REG-822', 'CN-ANHUI-5', 'CN-FORESTRY-PATROL'],
};
export const forestIssues: readonly ResearchIssue[] = forestEditorial.issues.map((issue) => ({
  key: issue.editorialKey,
  title: issue.title,
  summary: issue.summary,
  question: issue.question,
  media: issue.mediaPath.split('/').at(-1)!,
  information: issue.informationRequirements.map((row) => ({
    name: row.displayName,
    purpose: row.whyNeeded,
    acquisition: row.possibleSources,
    sourceIds: issue.officialSourceIds.slice(0, 2),
    detail: forestInformationDetails[informationDetailKey(issue.editorialKey, row.displayName)],
  })),
  capabilities: issue.capabilityContexts.map((context) => {
    const capability = forestEditorial.capabilities.find(
      (row) => row.editorialKey === context.editorialCapabilityKey,
    )!;
    return {
      key: context.editorialCapabilityKey,
      name: capability.displayNameZh,
      purpose: context.inThisIssuePurpose,
      output: context.expectedOutputInThisIssue,
      method: capability.whyInForest,
      limit:
        capability.editorialKey === 'CAP-SIGNAL'
          ? '热点不等于确证火灾或火线；烟点存在误判，不自动确认起火。'
          : capability.limitations,
      media: capability.illustration.split('/').at(-1)!,
      sourceIds: capabilitySources[capability.editorialKey] ?? [],
      formalCapabilityId: null,
    };
  }),
  sourceIds: issue.officialSourceIds,
  caseIds: issue.caseIds,
}));
export const defaultForestIssue = 'FOREST-ISSUE-01';
export function findForestIssue(key: string) {
  return forestIssues.find((issue) => issue.key === key);
}
